/**
 * Donor Cohorts API client (weavein) — Fundraising Suite, donor management.
 * Wraps /api/donor-cohorts/* (donor_cohorts + donor_cohort_members) via
 * authFetch (Authorization + X-CBO-ID). A cohort is a named, reusable donor
 * list (major donors, board, lapsed, event guest list) that can be bulk-invited
 * to a donor event.
 */

import { authFetchJson } from '@/lib/authFetch';

export type CohortMemberStatus = 'candidate' | 'selected' | 'invited' | 'accepted' | 'declined';

export interface CohortMember {
  member_id: number;
  name: string;
  email?: string | null;
  phone?: string | null;
  record_type?: string;
  /** Funnel state — membership used to be a bare join row with no status at all. */
  status: CohortMemberStatus;
  invited_at?: string | null;
  accepted_at?: string | null;
  source_label?: string | null;
  lifetime_giving?: number;
}

export interface DonorCohort {
  id: number;
  name: string;
  description?: string | null;
  member_count: number;
  invited_count: number;
  accepted_count: number;
  created_at?: string | null;
  members?: CohortMember[];
}

/** Where a cohort's candidates come from — the multi-channel identification the
 *  training cohort engine has and donor cohorts lacked entirely. */
export type CohortSourceType = 'group' | 'campaign' | 'event' | 'donors' | 'contacts';

export interface CohortSource {
  id: number;
  source_type: CohortSourceType;
  source_ref?: string | null;
  label: string;
  default_status: CohortMemberStatus;
  auto_sync: boolean;
  last_synced_at?: string | null;
  last_added: number;
}

export interface SourcePreview {
  source_type: CohortSourceType;
  label: string;
  resolved: number;
  already_in_cohort: number;
  would_add: number;
  sample: { member_id: number; name: string; email?: string | null }[];
}

export interface ContactLite {
  id: number;
  name: string;
  email?: string | null;
}

export async function listCohorts(): Promise<DonorCohort[]> {
  return authFetchJson<DonorCohort[]>('/api/donor-cohorts/cohorts');
}

export async function createCohort(payload: { name: string; description?: string }): Promise<DonorCohort> {
  return authFetchJson<DonorCohort>('/api/donor-cohorts/cohorts', { method: 'POST', jsonBody: payload });
}

export async function getCohort(id: number): Promise<DonorCohort> {
  return authFetchJson<DonorCohort>(`/api/donor-cohorts/cohorts/${id}`);
}

/** Add one donor or a batch. ANY member of the org qualifies — contacts and
 *  program members alike. */
export async function addCohortMembers(
  id: number,
  memberIds: number[],
  status: CohortMemberStatus = 'selected',
): Promise<{ added: number; requested: number; skipped_not_in_org: number }> {
  return authFetchJson(`/api/donor-cohorts/cohorts/${id}/members`, {
    method: 'POST',
    jsonBody: { member_ids: memberIds, status },
  });
}

export async function addCohortMember(id: number, memberId: number): Promise<unknown> {
  return addCohortMembers(id, [memberId]);
}

export async function updateCohort(
  id: number,
  payload: { name?: string; description?: string },
): Promise<DonorCohort> {
  return authFetchJson<DonorCohort>(`/api/donor-cohorts/cohorts/${id}`, { method: 'PATCH', jsonBody: payload });
}

/** Move one donor along the funnel — e.g. an acceptance taken by phone. */
export async function setCohortMemberStatus(
  cohortId: number,
  memberId: number,
  status: CohortMemberStatus,
): Promise<unknown> {
  return authFetchJson(`/api/donor-cohorts/cohorts/${cohortId}/members/${memberId}`, {
    method: 'PATCH',
    jsonBody: { status },
  });
}

// --- Candidate sources ---

export async function listCohortSources(id: number): Promise<CohortSource[]> {
  return authFetchJson<CohortSource[]>(`/api/donor-cohorts/cohorts/${id}/sources`);
}

export async function previewCohortSource(
  id: number,
  payload: { source_type: CohortSourceType; source_ref?: string },
): Promise<SourcePreview> {
  return authFetchJson<SourcePreview>(`/api/donor-cohorts/cohorts/${id}/sources/preview`, {
    method: 'POST',
    jsonBody: payload,
  });
}

export async function linkCohortSource(
  id: number,
  payload: {
    source_type: CohortSourceType;
    source_ref?: string;
    default_status?: CohortMemberStatus;
    auto_sync?: boolean;
  },
): Promise<{ id: number; label: string; added: number }> {
  return authFetchJson(`/api/donor-cohorts/cohorts/${id}/sources`, { method: 'POST', jsonBody: payload });
}

export async function syncCohortSource(id: number, sourceId: number): Promise<{ added: number }> {
  return authFetchJson(`/api/donor-cohorts/cohorts/${id}/sources/${sourceId}/sync`, { method: 'POST' });
}

export async function unlinkCohortSource(id: number, sourceId: number): Promise<void> {
  await authFetchJson<void>(`/api/donor-cohorts/cohorts/${id}/sources/${sourceId}`, { method: 'DELETE' });
}

export async function removeCohortMember(id: number, memberId: number): Promise<void> {
  await authFetchJson<void>(`/api/donor-cohorts/cohorts/${id}/members/${memberId}`, { method: 'DELETE' });
}

export async function deleteCohort(id: number): Promise<void> {
  await authFetchJson<void>(`/api/donor-cohorts/cohorts/${id}`, { method: 'DELETE' });
}

/**
 * Bulk-invite a cohort to an event — roster rows, funnel advance, AND delivery.
 * `invited` is roster rows added; `notified` is people actually reached. The old
 * response returned only the former while the UI called it "invited N guests".
 */
export async function inviteCohortToEvent(
  cohortId: number,
  eventId: number,
  payload: { channels?: ('email' | 'sms' | 'portal')[]; subject?: string; body?: string } = {},
): Promise<{ cohort_id: number; event_id: number; invited: number; notified: number; failures?: unknown[] }> {
  return authFetchJson(`/api/donor-cohorts/cohorts/${cohortId}/invite/${eventId}`, {
    method: 'POST',
    jsonBody: payload,
  });
}


/**
 * Donor/person search for the cohort picker.
 *
 * Delegates to the server-side people picker, which searches contacts AND
 * members. The previous implementation fetched `/api/members/?record_type=contact`
 * and filtered client-side: `record_type=contact` is a HARD filter on the members
 * endpoint, so program members who donate were invisible to the picker — the
 * reason members could not be added to donor cohorts — and the 200-row page cap
 * meant search silently missed people in any org with more contacts than that.
 */
export { searchPeople } from '@/services/api/donorProfiles';
export type { PersonResult } from '@/services/api/donorProfiles';

/** @deprecated Use `searchPeople` — this only ever returned contacts. */
export async function searchContacts(query: string): Promise<ContactLite[]> {
  const { searchPeople } = await import('@/services/api/donorProfiles');
  const rows = await searchPeople({ search: query, limit: 25 });
  return rows.map((r) => ({ id: r.id, name: r.name, email: r.email ?? null }));
}
